import type { CSSProperties } from 'react'
import { X } from 'lucide-react'
import { useLocale } from '../i18n/context'
import type { CardFilterChip } from '../utils/cardFilters'

interface Props {
  chip: CardFilterChip
  onChipChange: (chip: CardFilterChip) => void
  /** Deck-wide counts per chip, or `null` while they are not yet known. A
   * chip prints no number rather than the count of the pages this client has
   * loaded (brief §23 item 9) — these sit directly above a Fill action. */
  counts: Record<CardFilterChip, number> | null
  onClose: () => void
}

const CHIPS: CardFilterChip[] = ['all', 'no-translation', 'no-example', 'not-filled']

/**
 * The deck's filter chips (brief §16), split out of the old combined
 * search+filter bar (2026-08-04 rework). Search and the filter chips are
 * distinct per §13 — one is a transient navigational query, the other a
 * persistent narrowing of the feed — and bundling them into one panel is what
 * made that panel tall enough to crowd out search results under a raised
 * keyboard. This bar does one thing: show and change the active chip.
 *
 * Kept at its pre-existing sticky-in-feed position (unlike the search panel,
 * which moved to `position: fixed` under the top strip): a bar this size was
 * never the one reported opening off-screen, and closing the chip stays
 * active — this only toggles the bar's own visibility, not the filter. A
 * chip set here goes on filtering the feed after this bar closes, exactly as
 * it did before this split.
 */
export default function DeckFilterChipsBar({ chip, onChipChange, counts, onClose }: Props) {
  const { tr } = useLocale()
  const dm = tr.deckManagement

  const chipLabel: Record<CardFilterChip, string> = {
    all: dm.chipAll, 'no-translation': dm.chipNoTranslation,
    'no-example': dm.chipNoExample, 'not-filled': dm.chipNotFilled,
  }

  return (
    <div style={{ flexShrink: 0, background: 'var(--bg-surface)', borderBottom: '1px solid var(--border)' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 12px 8px' }}>
        <div style={{ display: 'flex', gap: 7, overflowX: 'auto', flex: 1 }}>
          {CHIPS.map((c) => {
            const active = c === chip
            return (
              <button key={c} onClick={() => onChipChange(c)} style={chipStyle(active)}>
                {chipLabel[c]}{c !== 'all' && counts ? ` (${counts[c]})` : ''}
              </button>
            )
          })}
        </div>
        <button onClick={onClose} aria-label={dm.closeFilterAria} style={closeBtnStyle}>
          <X size={16} />
        </button>
      </div>
    </div>
  )
}

const chipStyle = (active: boolean): CSSProperties => ({
  border: active ? '1px solid var(--accent-fill)' : '1px solid var(--border)',
  background: active ? 'var(--accent-fill)' : 'var(--bg-surface)',
  color: active ? 'var(--text-on-accent)' : 'var(--text-secondary)',
  borderRadius: 'var(--radius-pill)', padding: '6px 13px', cursor: 'pointer',
  fontFamily: 'var(--font-ui)', fontWeight: 600, fontSize: 12.5,
  whiteSpace: 'nowrap', flexShrink: 0,
})

const closeBtnStyle: CSSProperties = {
  width: 34, height: 34, borderRadius: 'var(--radius-pill)',
  border: '1px solid var(--border)', background: 'var(--bg-surface)',
  color: 'var(--text-secondary)', display: 'flex', alignItems: 'center',
  justifyContent: 'center', cursor: 'pointer', flexShrink: 0, padding: 0,
}
